import type { Cadence, ReportDetailDto } from '@act/shared/data-transfer-objects';
import {
  normalizeWarehouseMeasurementPeriod,
  parseReportingPeriodForPickers,
} from '@act/shared/data-transfer-objects';
import type { ReportReportingMonthOptionDto } from '@act/shared/data-transfer-objects';
import { useEffect } from 'react';

interface UseReportMetadataEditHydrationParams {
  mode: 'create' | 'edit';
  initialReport?: ReportDetailDto | null;
  customersLoading: boolean | null;
  customers: Array<{ id: string }>;
  reportingMonthOptions: ReportReportingMonthOptionDto[];
  hydratedReportId: string | null;
  setCustomerId: (value: string) => void;
  setCadence: (value: Cadence) => void;
  setMeasurementPeriod: (value: string) => void;
  setEndMonthKey: (value: string) => void;
  setMeasurementPeriodManuallyEdited: (value: boolean) => void;
  setName: (value: string) => void;
  setNameTouched: (value: boolean) => void;
  setHydratedReportId: (value: string) => void;
  loadMeasurementPeriods: (args: { params: { id: string } }) => void;
  loadReportingMonths: (args: { params: { id: string } }) => void;
}

/**
 * Hydrates report metadata form state when editing an existing report.
 * @param params - Edit form inputs and setters.
 */
export const useReportMetadataEditHydration = ({
  mode,
  initialReport,
  customersLoading,
  customers,
  reportingMonthOptions,
  hydratedReportId,
  setCustomerId,
  setCadence,
  setMeasurementPeriod,
  setEndMonthKey,
  setMeasurementPeriodManuallyEdited,
  setName,
  setNameTouched,
  setHydratedReportId,
  loadMeasurementPeriods,
  loadReportingMonths,
}: UseReportMetadataEditHydrationParams): void => {
  const isApiRequestPending = (loading: boolean | null): boolean =>
    loading !== false;

  useEffect(() => {
    if (
      mode !== 'edit' ||
      !initialReport ||
      hydratedReportId === initialReport.id ||
      isApiRequestPending(customersLoading)
    ) {
      return;
    }

    const customer = customers.find(item => item.id === initialReport.customerId);
    if (!customer) {
      return;
    }

    const reportCadence = initialReport.cadence;
    setCustomerId(initialReport.customerId);
    setCadence(reportCadence);
    setName(initialReport.name);
    setNameTouched(true);
    setHydratedReportId(initialReport.id);
    setMeasurementPeriodManuallyEdited(true);
    loadMeasurementPeriods({ params: { id: initialReport.customerId } });

    if (reportCadence === 'adhoc') {
      const normalizedMeasurementPeriod = normalizeWarehouseMeasurementPeriod(
        initialReport.measurementPeriod ?? initialReport.reportingPeriod ?? '',
      );
      setMeasurementPeriod(normalizedMeasurementPeriod);
      return;
    }

    loadReportingMonths({ params: { id: initialReport.customerId } });
    if (initialReport.measurementPeriod) {
      setMeasurementPeriod(
        normalizeWarehouseMeasurementPeriod(initialReport.measurementPeriod),
      );
    }
    const monthKeys = reportingMonthOptions.map(option => option.value);
    const parsed = parseReportingPeriodForPickers(
      reportCadence,
      initialReport.reportingPeriod,
      monthKeys,
    );
    setEndMonthKey(parsed.endMonth);
  }, [
    customers,
    customersLoading,
    hydratedReportId,
    initialReport,
    loadMeasurementPeriods,
    loadReportingMonths,
    mode,
    reportingMonthOptions,
    setCadence,
    setCustomerId,
    setEndMonthKey,
    setHydratedReportId,
    setMeasurementPeriod,
    setMeasurementPeriodManuallyEdited,
    setName,
    setNameTouched,
  ]);

  useEffect(() => {
    if (
      mode !== 'edit' ||
      !initialReport ||
      hydratedReportId !== initialReport.id ||
      initialReport.cadence === 'adhoc' ||
      reportingMonthOptions.length === 0
    ) {
      return;
    }
    const monthKeys = reportingMonthOptions.map(option => option.value);
    const parsed = parseReportingPeriodForPickers(
      initialReport.cadence,
      initialReport.reportingPeriod,
      monthKeys,
    );
    if (parsed.endMonth) {
      setEndMonthKey(parsed.endMonth);
    }
  }, [
    hydratedReportId,
    initialReport,
    mode,
    reportingMonthOptions,
    setEndMonthKey,
  ]);
};
